// Challenges and rematches: what the server says about one, and how its link is sent to Telegram, WhatsApp or X.
import { api, credentials } from "./api.js";
import { tg } from "./telegram.js";

export interface Challenge {
  id: string;
  kind: "link" | "rematch";
  state: "open" | "accepted" | "cancelled" | "expired" | "declined";
  closeReason: string | null;
  arena: string;
  durationSec: number;
  stake: string;
  /** The creator's side; whoever accepts plays the other one. */
  side: "long" | "short";
  creator: { id: string; name: string };
  target: string | null;
  fromBattle: string | null;
  /** Server time. */
  expiresAt: number;
  /** How long it was open in all: 5 minutes, or 1 for a rematch while both players were in the app. */
  openMs: number;
  battleId: string | null;
  /** A rematch only: the two players' record, and who won the battle it follows. */
  rivalry?: { creatorWins: number; targetWins: number; rounds: number; lastWinner: "creator" | "target" | null };
  /** A rematch only: the other player had left the app, so the bot messaged them. */
  notified?: boolean;
  /** Opens the challenge in the Mini App. */
  link?: string;
  /** Our page: WhatsApp and X show the challenge picture as its preview. */
  url?: string;
  image?: string;
}

export const arenaLabel = (arena: string) => (arena === "XAU" ? "GOLD" : arena);
export const stakeLabel = (stake: string) => stake.replace(/\.0+$/, "");
export const otherSide = (side: "long" | "short") => (side === "long" ? "short" : "long");

/** The link challenge this phone was opened with (t.me/bot?startapp=c_<id>), kept until its screen reads it. */
const OPENED = "dd:openChallenge";
/** The player whose revenge messages to stop (t.me/bot?startapp=m_<id>), kept until the lobby does it. */
export const MUTE = "dd:muteRival";
/** Which alert a bot message's Stop button turns off (a_w waiting, a_p power hour), kept until the lobby does it. */
export const ALERT_OFF = "dd:alertOff";
/** The search a waiting alert's button answers (w_<arena>_<L|S>_<stake in cents>_<seconds>), kept for its screen. */
export const WAITING_ALERT = "dd:waitingAlert";
export interface WaitingAlert {
  arena: string;
  /** The waiting player's side; the one who taps takes the other. */
  side: "long" | "short";
  stake: string;
  durationSec: number;
}
export function rememberOpenedChallenge(): void {
  const param = tg?.initDataUnsafe.start_param ?? new URLSearchParams(location.search).get("tgWebAppStartParam") ?? "";
  const mute = param.match(/^m_([0-9]{1,20})$/);
  // "Stop revenge messages" in a bot message opens the app with the player to mute.
  if (mute && sessionStorage.getItem(`${MUTE}:seen`) !== mute[1]) {
    sessionStorage.setItem(MUTE, mute[1]!);
    sessionStorage.setItem(`${MUTE}:seen`, mute[1]!);
  }
  // "Invite more friends" in the bot's message: the Invite friends screen.
  if (param === "invite" && !sessionStorage.getItem("dd:openInvite:seen")) {
    sessionStorage.setItem("dd:openInvite", "1");
    sessionStorage.setItem("dd:openInvite:seen", "1");
  }
  // A partner link (t.me/bot?startapp=partners): the partner application.
  if (param === "partners" && !sessionStorage.getItem("dd:openPartners:seen")) {
    sessionStorage.setItem("dd:openPartners", "1");
    sessionStorage.setItem("dd:openPartners:seen", "1");
  }
  // "See Events" in a tournament message: the Events tab.
  if (param === "events" && !sessionStorage.getItem("dd:openEvents:seen")) {
    sessionStorage.setItem("dd:openEvents", "1");
    sessionStorage.setItem("dd:openEvents:seen", "1");
  }
  // "See the new week" in the prize message: the Ranks screen.
  if (param === "ranks" && !sessionStorage.getItem("dd:openRanks:seen")) {
    sessionStorage.setItem("dd:openRanks", "1");
    sessionStorage.setItem("dd:openRanks:seen", "1");
  }
  const off = param.match(/^a_(w|p)$/);
  if (off && !sessionStorage.getItem(`${ALERT_OFF}:seen`)) {
    sessionStorage.setItem(ALERT_OFF, off[1] === "w" ? "waiting" : "power");
    sessionStorage.setItem(`${ALERT_OFF}:seen`, "1");
  }
  const wait = param.match(/^w_([A-Z]{2,4})_(L|S)_([0-9]{1,7})_(60|180)$/);
  if (wait && sessionStorage.getItem(`${WAITING_ALERT}:seen`) !== param) {
    const alert: WaitingAlert = { arena: wait[1]!, side: wait[2] === "L" ? "long" : "short", stake: String(Number(wait[3]) / 100), durationSec: Number(wait[4]) };
    sessionStorage.setItem(WAITING_ALERT, JSON.stringify(alert));
    sessionStorage.setItem(`${WAITING_ALERT}:seen`, param);
  }
  const m = param.match(/^c_([A-Za-z0-9]{10})$/);
  // Telegram keeps start_param for the whole session: a reload must not open the same challenge again.
  if (m && sessionStorage.getItem(`${OPENED}:seen`) !== m[1]) {
    sessionStorage.setItem(OPENED, m[1]!);
    sessionStorage.setItem(`${OPENED}:seen`, m[1]!);
  }
}
export function openedChallenge(): string | null {
  return sessionStorage.getItem(OPENED);
}
export function setOpenedChallenge(id: string | null): void {
  if (id) sessionStorage.setItem(OPENED, id);
  else sessionStorage.removeItem(OPENED);
}

/**
 * Cancels when the app is closed: a normal request can be cut off as the page goes, a keepalive one is still sent.
 * Telegram doesn't always tell a closing app, so the 5 minute limit is the backstop.
 */
export function cancelOnClose(id: string): void {
  const auth = credentials();
  void fetch(`/v1/challenges/${id}/cancel`, { method: "POST", keepalive: true, headers: auth ? { authorization: `tma ${auth}` } : {} }).catch(() => {});
}

/** What goes with the link: the call, and what taking it means. */
export function challengeCaption(c: Challenge): string {
  const minutes = c.durationSec === 60 ? "1 minute" : "3 minutes";
  const way = c.side === "long" ? "UP" : "DOWN";
  return `I say ${arenaLabel(c.arena)} goes ${way} in ${minutes}. Think I'm wrong? Take ${otherSide(c.side).toUpperCase()} for ${stakeLabel(c.stake)} USDT. First to accept plays.`;
}

const open = (url: string) => (tg?.openLink ? tg.openLink(url) : void window.open(url, "_blank"));
const openTelegram = (url: string) => (tg?.openTelegramLink ? tg.openTelegramLink(url) : void window.open(url, "_blank"));

/** Telegram's own chat picker with the picture and an Accept button; a plain link share on older apps. */
export async function sendToTelegram(c: Challenge): Promise<void> {
  const caption = challengeCaption(c);
  if (tg?.shareMessage && tg.isVersionAtLeast("8.0")) {
    try {
      const { preparedId } = await api<{ preparedId: string }>(`/v1/challenges/${c.id}/telegram`, { method: "POST", body: { caption } });
      tg.shareMessage(preparedId);
      return;
    } catch {
      /* the bot couldn't prepare it: a link share below */
    }
  }
  openTelegram(`https://t.me/share/url?url=${encodeURIComponent(c.link ?? "")}&text=${encodeURIComponent(caption)}`);
}

export function sendToWhatsApp(c: Challenge): void {
  open(`https://wa.me/?text=${encodeURIComponent(`${challengeCaption(c)}\n${c.url ?? ""}`)}`);
}

/** X shows the bot link in the post and the picture from our page, as for share cards. */
export function sendToX(c: Challenge): void {
  open(`https://x.com/intent/post?text=${encodeURIComponent(`${challengeCaption(c)}\n${c.link ?? ""}`)}&url=${encodeURIComponent(c.url ?? "")}`);
}

/** Asks once to let the bot message this player, so "your challenge was taken" can reach them after they leave. */
export function askToMessage(): void {
  try {
    if (localStorage.getItem("dd.writeAsked")) return;
    localStorage.setItem("dd.writeAsked", "1");
  } catch {
    return;
  }
  if (tg?.requestWriteAccess && tg.isVersionAtLeast("6.9")) tg.requestWriteAccess();
}

/** Our record against another player: wins, losses, and battles played (draws count as a round, refunds don't). */
export interface Rival {
  wins: number;
  losses: number;
  rounds: number;
  muted: boolean;
}

const rivals = new Map<string, Promise<Rival | null>>();
/** Asked for when a battle starts, so the Versus flash has the score at once. Battles change it, so it is asked again. */
export function fetchRival(id: string): Promise<Rival | null> {
  const p = api<Rival>(`/v1/rivals/${id}`).catch(() => null);
  rivals.set(id, p);
  return p;
}
export function rivalOf(id: string): Promise<Rival | null> {
  return rivals.get(id) ?? fetchRival(id);
}

/** "lost round 5", "won round 5" or "drew round 5", from the reader's side. */
export function lastRound(c: Challenge): string {
  const r = c.rivalry;
  if (!r) return "";
  const n = Math.max(1, r.rounds);
  return r.lastWinner === "target" ? `lost round ${n}` : r.lastWinner === "creator" ? `won round ${n}` : `drew round ${n}`;
}
